<template>
	<input
		ref="inputRef"
		type="text"
		class="p-inputtext"
		:class="inputClass"
		:placeholder="placeholder"
		:disabled="disabled"
		:value="displayValue"
		@input="onInput"
		@blur="onBlur"
		@keydown="onKeyDown"
	/>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue"

const props = withDefaults(
	defineProps<{
		modelValue?: number | string | null
		maxDigits?: number
		maxDecimalDigits?: number
		placeholder?: string
		inputClass?: string | Record<string, boolean>
		disabled?: boolean
		allowNegative?: boolean
	}>(),
	{
		modelValue: null,
		maxDigits: 38,
		maxDecimalDigits: 20,
		placeholder: "",
		disabled: false,
		allowNegative: true,
	}
)

const emit = defineEmits<{
	"update:modelValue": [value: number | null]
	"update:rawModelValue": [value: string | null]
}>()

const inputRef = ref<HTMLInputElement>()
const internalValue = ref("")

const displayValue = computed(() => internalValue.value)

// Expose raw string value for high-precision serialization
const rawValue = computed(() => {
	const val = internalValue.value.trim()
	if (val === "" || val === "." || val === "-" || val === "-.") return null
	return val
})

function syncFromModel() {
	if (props.modelValue === null || props.modelValue === undefined) {
		internalValue.value = ""
	} else if (typeof props.modelValue === "number") {
		const str = String(props.modelValue)
		// Avoid scientific notation (e.g. 1e-9) for display
		if (str.includes("e") || str.includes("E")) {
			internalValue.value = props.modelValue.toFixed(20).replace(/\.?0+$/, "")
		} else {
			internalValue.value = str
		}
	} else {
		internalValue.value = String(props.modelValue)
	}
}

watch(
	() => props.modelValue,
	() => {
		if (document.activeElement !== inputRef.value) {
			syncFromModel()
		}
	},
	{ immediate: true }
)

function countDigits(val: string): number {
	return val.replace(/[^0-9]/g, "").length
}

function isValidPartial(val: string): boolean {
	if (val === "" || val === "." || val === "-" || val === "-.") return true
	const pattern = props.allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/
	return pattern.test(val)
}

// Reverts a rejected edit while keeping the cursor where the user was typing,
// instead of letting the browser's value reset jump it to the end.
function revertInput(target: HTMLInputElement) {
	const cursorPos = Math.max(0, (target.selectionStart ?? internalValue.value.length) - 1)
	target.value = internalValue.value
	target.setSelectionRange(cursorPos, cursorPos)
}

function onKeyDown(event: KeyboardEvent) {
	const allowed = [
		"Backspace",
		"Delete",
		"Tab",
		"ArrowLeft",
		"ArrowRight",
		"ArrowUp",
		"ArrowDown",
		"Home",
		"End",
		"Enter",
	]
	if (allowed.includes(event.key)) return
	if (event.ctrlKey || event.metaKey) return
	if (event.key === "." && !internalValue.value.includes(".")) return
	if (event.key === "-" && props.allowNegative && !internalValue.value.includes("-") && (inputRef.value?.selectionStart === 0 || internalValue.value === "")) return
	if (event.key >= "0" && event.key <= "9") return
	event.preventDefault()
}

function onInput(event: Event) {
	const target = event.target as HTMLInputElement
	let val = target.value

	if (!isValidPartial(val)) {
		revertInput(target)
		return
	}

	if (countDigits(val) > props.maxDigits) {
		revertInput(target)
		return
	}

	const dotIndex = val.indexOf(".")
	if (dotIndex !== -1 && val.length - dotIndex - 1 > props.maxDecimalDigits) {
		revertInput(target)
		return
	}

	const integerPart = dotIndex === -1 ? val : val.slice(0, dotIndex)
	const maxIntegerDigits = props.maxDigits - props.maxDecimalDigits
	if (countDigits(integerPart) > maxIntegerDigits) {
		revertInput(target)
		return
	}

	internalValue.value = val

	if (val === "" || val === "." || val === "-" || val === "-.") {
		emit("update:modelValue", null)
		emit("update:rawModelValue", null)
	} else {
		const parsed = parseFloat(val)
		if (!isNaN(parsed)) {
			emit("update:modelValue", parsed)
		}
		emit("update:rawModelValue", val)
	}
}

function onBlur() {
	let val = internalValue.value.trim()

	if (val === "" || val === "." || val === "-" || val === "-.") {
		internalValue.value = ""
		emit("update:modelValue", null)
		emit("update:rawModelValue", null)
		return
	}

	if (val.endsWith(".")) {
		val = val.slice(0, -1)
	}
	if (val.startsWith(".")) {
		val = "0" + val
	}
	if (val.startsWith("-.")) {
		val = "-0" + val.slice(1)
	}

	internalValue.value = val
	const parsed = parseFloat(val)
	if (!isNaN(parsed)) {
		emit("update:modelValue", parsed)
	}
	emit("update:rawModelValue", val)
}

defineExpose({
	$el: inputRef,
	rawValue,
})
</script>
